import React from 'react';
import { useCSVStore } from '../../stores/csvStore';
import './SubmissionModeSelector.css';

const SubmissionModeSelector: React.FC = () => {
  const { 
    isMultiLocationMode, 
    setMultiLocationMode,
    isMultiPeriodMode,
    setMultiPeriodMode,
    accessibleLocations,
    selectedLocation,
    detectedDateRange
  } = useCSVStore();

  const handleLocationModeChange = (isMulti: boolean) => {
    setMultiLocationMode(isMulti);
  };

  const handlePeriodModeChange = (isMulti: boolean) => {
    setMultiPeriodMode(isMulti);
  };

  // Calculate estimated submissions for multi-period mode
  const calculateEstimatedSubmissions = () => {
    if (!isMultiPeriodMode || !detectedDateRange) return 0;
    
    const monthCount = calculateMonthCount();
    const locationCount = isMultiLocationMode ? accessibleLocations.length : 1;
    
    return monthCount * locationCount;
  };

  const calculateMonthCount = () => {
    if (!detectedDateRange) return 0;
    
    const { startYear, startMonth, endYear, endMonth } = detectedDateRange;
    const startDate = new Date(startYear, startMonth - 1, 1);
    const endDate = new Date(endYear, endMonth - 1, 1);
    
    let months = 0;
    let currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      months++;
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return months;
  };

  return (
    <div className="submission-mode-selector">
      <h3>Submission Configuration</h3>
      
      <div className="mode-selection-grid">
        {/* Location Mode Selection */}
        <div className="mode-group">
          <h4>Location Mode</h4>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="locationMode"
                checked={!isMultiLocationMode}
                onChange={() => handleLocationModeChange(false)}
              />
              <span className="radio-label">
                <strong>Single Location</strong>
                <small>Submit data for one selected location</small>
              </span>
            </label>
            
            <label className="radio-option">
              <input
                type="radio"
                name="locationMode"
                checked={isMultiLocationMode}
                onChange={() => handleLocationModeChange(true)}
                disabled={accessibleLocations.length <= 1}
              />
              <span className="radio-label">
                <strong>Multiple Locations</strong>
                <small>
                  {accessibleLocations.length <= 1 
                    ? 'Not available (only 1 location accessible)'
                    : `Submit data for multiple locations (${accessibleLocations.length} available)`
                  }
                </small>
              </span>
            </label>
          </div>
        </div>

        {/* Period Mode Selection */}
        <div className="mode-group">
          <h4>Time Period Mode</h4>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="periodMode"
                checked={!isMultiPeriodMode}
                onChange={() => handlePeriodModeChange(false)}
              />
              <span className="radio-label">
                <strong>Single Period</strong>
                <small>Submit data for one month/year</small>
              </span>
            </label>
            
            <label className="radio-option">
              <input
                type="radio"
                name="periodMode"
                checked={isMultiPeriodMode}
                onChange={() => handlePeriodModeChange(true)}
              />
              <span className="radio-label">
                <strong>Multiple Periods</strong>
                <small>Automatically detect date range from CSV Year/Month columns</small>
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Submission Summary */}
      {(isMultiLocationMode || isMultiPeriodMode) && (
        <div className="submission-summary">
          <h4>Submission Summary</h4>
          <div className="summary-stats">
            <div className="stat">
              <span className="stat-label">Locations:</span>
              <span className="stat-value">
                {isMultiLocationMode ? accessibleLocations.length : 1}
                {isMultiLocationMode && !selectedLocation && ' (all accessible)'}
                {!isMultiLocationMode && selectedLocation && ` (${selectedLocation.name})`}
              </span>
            </div>
            
            <div className="stat">
              <span className="stat-label">Time Periods:</span>
              <span className="stat-value">
                {isMultiPeriodMode && detectedDateRange 
                  ? `${calculateMonthCount()} months`
                  : '1 month'
                }
              </span>
            </div>
            
            <div className="stat total-stat">
              <span className="stat-label">Total Submissions:</span>
              <span className="stat-value">
                {isMultiPeriodMode ? calculateEstimatedSubmissions() : 1}
              </span>
            </div>
          </div>
          
          {isMultiPeriodMode && calculateEstimatedSubmissions() > 100 && (
            <div className="warning-message">
              ⚠️ Large submission detected ({calculateEstimatedSubmissions()} submissions). 
              This may take several minutes to process.
            </div>
          )}
        </div>
      )}

      {/* Mode Descriptions */}
      <div className="mode-descriptions">
        <div className="description-card">
          <h5>Current Configuration:</h5>
          <p>
            <strong>
              {isMultiLocationMode ? 'Multi-Location' : 'Single-Location'}, {' '}
              {isMultiPeriodMode ? 'Multi-Period' : 'Single-Period'}
            </strong>
          </p>
          <ul>
            {!isMultiLocationMode && (
              <li>CSV should contain KPI data for one location</li>
            )}
            {isMultiLocationMode && (
              <li>CSV must include a "Location" or "Dealer" column</li>
            )}
            {!isMultiPeriodMode && (
              <li>All data will be submitted for the selected month/year</li>
            )}
            {isMultiPeriodMode && (
              <li>CSV must include "Year" and "Month" columns</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubmissionModeSelector;
